import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  IconButton,
  Grid,
  Card,
  CardContent,
  Radio,
  Button,
  Divider,
  useTheme,
  Checkbox,
  Chip
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import CloseIcon from '@mui/icons-material/Close'
import { useNavigate, useLocation } from 'react-router-dom'
import aiIcon from 'src/assets/siri.png'
import { planDetail } from 'src/assets/data'
import { customAddOns } from './UsageData'
import { styled } from '@mui/material/styles'
import FormControlLabel from '@mui/material/FormControlLabel'
import Switch from '@mui/material/Switch'
import AddCreditsSlider from './AddCreditsSlider'

const ADD_ON_PRICES = {
  dfyAccounts: (mailboxes = 3, domains = 1) => (3.5 * mailboxes) + (12 * domains),
  mailbox: 1,
  emailVerification: 10,
  vvSupport: 0,
  visitorIdentification: 0,
  sms: 0,
  linkedIn: 20,
  whatsapp: 20
}

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName='.Mui-focusVisible' disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 26,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(16px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#65C466',
        opacity: 1,
        border: 0,
        ...theme.applyStyles('dark', {
          backgroundColor: '#2ECA45'
        })
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.5
      }
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff'
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.grey[100],
      ...theme.applyStyles('dark', {
        color: theme.palette.grey[600]
      })
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: 0.7,
      ...theme.applyStyles('dark', {
        opacity: 0.3
      })
    }
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 22,
    height: 22
  },
  '& .MuiSwitch-track': {
    borderRadius: 26 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 500
    }),
    ...theme.applyStyles('dark', {
      backgroundColor: '#39393D'
    })
  }
}))

const CustomizePlan = () => {
  const location = useLocation()
  const state = location.state
  const [selectedPlan, setSelectedPlan] = useState(state.price_id)
  const [selectedAddOns, setSelectedAddOns] = useState([])
  const [selectedAddOnsPrice, setSelectedAddOnsPrice] = useState(0)
  const [updatedPrice, setUpdatedPrice] = useState(getPriceById(state.price_id))
  const [total, setTotal] = useState(0)
  const [isSwitchOn, setIsSwitchOn] = useState(false)
  const [emailCreditsDetails, setEmailCreditsDetails] = useState({
    credits: 1000,
    period: 'monthly',
    price: 4
  })
  const [monthlyPrice, setMonthlyPrice] = useState(0)
  const theme = useTheme()
  const navigate = useNavigate()
  function getPriceById (priceId) {
    for (const plan of Object.values(state.package
      ? planDetail[state.type].MONTHLY
      : planDetail[state.type].YEARLY)) {
      if (plan.priceId === priceId) {
        return plan.price
      }
    }
    return null
  }
  const handleSelect = (id, price) => {
    setSelectedPlan(id)
    setUpdatedPrice(price)
  }
  const planCard = [{
    leadsPlan: state.package
      ? planDetail.LEADS.MONTHLY
      : planDetail.LEADS.YEARLY,
    outreachPlan: state.package
      ? planDetail.OUTREACH.MONTHLY
      : planDetail.OUTREACH.YEARLY
  }]

  const handleToggleAddOn = (id) => {
    setSelectedAddOns((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id]
    )
  }

  useEffect(() => {
    const calculateTotal = () => {
      return selectedAddOns.reduce((acc, id) => {
        if (id === 'emailVerification') {
          acc += emailCreditsDetails?.price || 0
        } else {
          const price = ADD_ON_PRICES[id]
          if (typeof price === 'function') {
            acc += price()
          }
          if (typeof price === 'number') {
            acc += price
          }
        }
        return acc
      }, 0)
    }
    setSelectedAddOnsPrice(calculateTotal())
  }, [selectedAddOns, emailCreditsDetails])

  const getPlanNameByPriceId = (priceId) => {
    for (const [planName, details] of Object.entries(planDetail[state.type].YEARLY)) {
      if (details.priceId === priceId) {
        return planName
      }
    }
    return null
  }

  useEffect(() => {
    const planName = getPlanNameByPriceId(state.price_id)
    setMonthlyPrice(selectedAddOnsPrice + (isSwitchOn
      ? (12 * parseFloat(planDetail[state.type].MONTHLY[planName]?.price.replace(/[^0-9.]/g, '') || 0))
      : parseFloat(planDetail[state.type].MONTHLY[planName]?.price.replace(/[^0-9.]/g, '') || 0)))
    setTotal(selectedAddOnsPrice + (isSwitchOn ? (12 * parseFloat(updatedPrice.replace(/[^0-9.]/g, ''))) : parseFloat(updatedPrice.replace(/[^0-9.]/g, ''))))
  }, [total, selectedAddOnsPrice, updatedPrice, isSwitchOn])

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          bgcolor: 'background.default',
          px: 2,
          pt: 2,
          maxWidth: '80%',
          m: '0 auto',
          maxHeight: '95%',
          justifyContent: 'space-between',
          pb: { xs: '250px', sm: '250px', md: '70px', lg: '70px', xl: '70px' }
        }}
      >
        {/* Header */}
        <Box sx={{ position: 'relative' }}>
          <IconButton sx={{ position: 'absolute', left: 0, top: 0 }} color='primary' onClick={() => navigate('/settingsUpdated')}>
            <ArrowBackIcon />
          </IconButton>

          <Typography
            variant='h6'
            align='center'
            sx={{ fontWeight: 'bold' }}
          >
            Build your own plan
          </Typography>

          <IconButton sx={{ position: 'absolute', right: 0, top: 0 }} onClick={() => navigate('/settingsUpdated')}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box>
          <Box
            sx={{
              display: 'flex',
              mt: 4,
              mb: 1
            }}
          >
            <img src={aiIcon} alt='aiIcon' width={30} />
            <Typography variant='h6'>Leads</Typography>
          </Box>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {Object.entries(planCard[0].leadsPlan).map(([planName, planData], i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Card
                  variant='outlined'
                  sx={{
                    borderColor: planData.priceId === selectedPlan ? theme.palette.primary.main : 'divider',
                    borderWidth: planData.priceId === selectedPlan ? 2 : 1,
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => handleSelect(planData.priceId, planData.price)}
                >
                  <CardContent>
                    <Radio
                      checked={selectedPlan === planData.priceId}
                      value={planData.priceId}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                      readOnly
                    />
                    <Typography variant='h6' sx={{ fontSize: { xs: '13px', sm: '13px', md: '13px', lg: '14px', xl: '14px' } }}>{planName.charAt(0) + planName.slice(1).toLowerCase()}</Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '13px', sm: '13px', md: '13px', lg: '14px', xl: '14px' } }}>
                      {planData.price}/month
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>))}
          </Grid>
          <Box
            sx={{
              display: 'flex',
              mt: 4,
              mb: 1
            }}
          >
            <img src={aiIcon} alt='aiIcon' width={30} />
            <Typography variant='h6'>Outreach</Typography>
          </Box>
          <Grid container spacing={2} sx={{ flexGrow: 1 }}>
            {Object.entries(planCard[0].outreachPlan).map(([planName, planData], i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Card
                  variant='outlined'
                  sx={{
                    borderColor: planData.priceId === selectedPlan ? theme.palette.primary.main : 'divider',
                    borderWidth: planData.priceId === selectedPlan ? 2 : 1,
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => handleSelect(planData.priceId, planData.price)}
                >
                  <CardContent>
                    <Radio
                      checked={selectedPlan === planData.priceId}
                      value={planData.priceId}
                      sx={{ position: 'absolute', top: 8, right: 8 }}
                      readOnly
                    />
                    <Typography variant='h6' sx={{ fontSize: { xs: '13px', sm: '13px', md: '13px', lg: '14px', xl: '14px' } }}>{planName.charAt(0) + planName.slice(1).toLowerCase()}</Typography>
                    <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '13px', sm: '13px', md: '13px', lg: '14px', xl: '14px' } }}>
                      {planData.price}/month
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>))}
          </Grid>
        </Box>
        <Box>
          <Typography
            variant='h6'
            sx={{
              mt: 5
            }}
          >2. Select add-ons
          </Typography>
          <Box
            sx={{
              mt: 2
            }}
          >
            <Grid container spacing={2} sx={{ flexGrow: 1 }}>
              {customAddOns.map((adOn) => (
                <Grid item xs={12} sm={12} key={adOn.id}>
                  <Card
                    variant='outlined'
                    sx={{
                      borderColor: adOn.id === selectedPlan ? theme.palette.primary.main : 'divider',
                      borderWidth: adOn.id === selectedPlan ? 2 : 1,
                      cursor: 'pointer',
                      position: 'relative'
                    }}
                  >
                    <CardContent>
                      <Checkbox
                        checked={Array.isArray(selectedAddOns) && selectedAddOns.includes(adOn.id)}
                        value={adOn.id}
                        onChange={() => handleToggleAddOn(adOn.id)}
                        sx={{ position: 'absolute', top: 8, right: 8 }}
                      />
                      <Typography variant='h6'>{adOn.title} {' '}
                        <Chip
                          label={`${adOn.price}/year`}
                          sx={{
                            backgroundColor: '#216fed',
                            color: '#fff',
                            fontWeight: 'bold'
                          }}
                        />
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {adOn.description}
                      </Typography>
                    </CardContent>
                  </Card>
                  {adOn.id === 'emailVerification' && selectedAddOns.includes('emailVerification') && (
                    <Box mt={2}>
                      <AddCreditsSlider onPriceChange={(details) => setEmailCreditsDetails(details)} />
                    </Box>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>

      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: { xs: 0, sm: 0, md: 100, lg: 100 },
          width: { xs: '100%', sm: '100%', md: '90%', lg: '90%', xl: '90%' },
          bgcolor: 'background.paper',
          boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
          p: 2,
          zIndex: 1000
        }}
      >
        <Box display='flex' justifyContent='space-between' alignItems='center' flexWrap='wrap'>
          <Box>
            <Typography variant='body1' fontWeight='bold'>
              Summary
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Your Plan
            </Typography>
          </Box>
          <Box display='flex' alignItems='center' gap={2} flexWrap='wrap'>
            <FormControlLabel
              control={<IOSSwitch checked={isSwitchOn} onChange={(e) => setIsSwitchOn(e.target.checked)} />}
              sx={{ marginRight: 0 }}

            />
            <Typography variant='body2'>Yearly</Typography>
            <Chip
              label='3 months FREE'
              sx={{
                backgroundColor: '#10b523',
                color: '#fff'
              }}
            />
            <Divider orientation='vertical' flexItem sx={{ borderColor: 'grey.400' }} />

            <Box>
              <Typography variant='body2' color='text.secondary'>
                Billed yearly
              </Typography>
              <Box
                sx={{
                  display: 'flex'
                }}
              >
                <Typography
                  variant='body2' fontWeight='bold' color='text.secondary'
                  sx={{ textDecoration: 'line-through', opacity: 0.6 }}
                >
                  ${monthlyPrice}
                </Typography>
                <Typography
                  variant='body2' fontWeight='bold'
                  sx={{ ml: 1 }}
                >
                  ${total}/mo
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box ml={{ xs: 0, md: 2 }} mt={{ xs: 2, md: 0 }}>
            <Button variant='contained' size='large'>
              Continue to Checkout – ${total}
            </Button>
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default CustomizePlan
